<template>
  <div class="pdf-viewer-container">
    <div v-if="loading" class="pdf-loading">
      <i class="fa fa-spinner fa-spin"></i>
      <span>Loading Project...</span>
    </div>

    <div v-if="error" class="pdf-error">
      <i class="fa fa-exclamation-circle"></i>
      <span>{{ error }}</span>
    </div>

    <div
      v-show="!loading && !error"
      class="pdf-scroll-area"
      ref="scrollContainer">
      <div
        v-for="page in pages"
        :key="page.pageNumber"
        class="pdf-page-container"
        :style="{ height: page.height + 'px', width: page.width + 'px' }"
        :data-page-number="page.pageNumber">
        <canvas :ref="'pageCanvas' + page.pageNumber"></canvas>
        <div class="textLayer" :ref="'textLayer' + page.pageNumber"></div>
        <div v-if="!page.rendered" class="page-loading-placeholder">
          <i class="fa fa-circle-o-notch fa-spin"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjs from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry.js";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: "PDFViewer",
  props: {
    pdfUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      pdfDocument: null,
      pages: [], // Array of { pageNumber, width, height, rendered }
      scale: 1, // Base scale, can be adjusted
      loading: true,
      error: null,
      observer: null,
      resizeObserver: null,
      resizeTimeout: null,
      originalViewport: null,
    };
  },
  watch: {
    pdfUrl: {
      immediate: true,
      handler(newUrl) {
        if (newUrl) {
          this.loadPdf(newUrl);
        }
      },
    },
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.onResize();
      }, 500);
    });
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    // Cleanup PDF document
    if (this.pdfDocument) {
      this.pdfDocument.destroy();
    }
  },
  methods: {
    async loadPdf(url) {
      this.loading = true;
      this.error = null;
      this.pages = [];

      if (this.pdfDocument) {
        this.pdfDocument.destroy();
        this.pdfDocument = null;
      }

      try {
        // Load the PDF document
        const loadingTask = pdfjs.getDocument(url);
        this.pdfDocument = await loadingTask.promise;

        // Process first page to get dimensions and calculate layout
        const firstPage = await this.pdfDocument.getPage(1);
        this.originalViewport = firstPage.getViewport({ scale: 1 });
        this.updateScale();
        const viewport = firstPage.getViewport({ scale: this.scale });

        // Initialize page placeholders
        // We assume all pages are roughly the same size for the skeleton,
        // but we'll update exact sizes when we actually load them if needed.
        // For simplicity and performance, using first page size for container is usually sufficient for projects.
        for (let i = 1; i <= this.pdfDocument.numPages; i++) {
          this.pages.push({
            pageNumber: i,
            width: viewport.width,
            height: viewport.height,
            rendered: false,
          });
        }

        this.loading = false;

        // Initialize IntersectionObserver after DOM update
        this.$nextTick(() => {
          this.initObserver();
        });
      } catch (err) {
        console.error("Error loading PDF:", err);
        this.error = "Failed to load document.";
        this.loading = false;
      }
    },

    updateScale() {
      if (!this.originalViewport || !this.$el) return;
      const containerWidth = this.$el.clientWidth;
      if (containerWidth > 0) {
        this.scale = containerWidth / this.originalViewport.width;
      }
    },

    onResize() {
      if (!this.pdfDocument || !this.originalViewport) return;

      const oldScale = this.scale;
      this.updateScale();

      // Only update if scale changed significantly
      if (Math.abs(this.scale - oldScale) > 0.001) {
        const newWidth = this.originalViewport.width * this.scale;
        const newHeight = this.originalViewport.height * this.scale;

        this.pages.forEach((page) => {
          page.width = newWidth;
          page.height = newHeight;
          page.rendered = false;
        });

        // Reset observer to trigger render for visible pages
        if (this.observer) {
          this.observer.disconnect();
          this.initObserver();
        }
      }
    },

    initObserver() {
      // Setup IntersectionObserver to lazy load pages
      const options = {
        root: null,
        rootMargin: "200px", // Pre-load pages 200px before they come into view
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver(this.handleIntersect, options);

      // Observe all page containers
      const pageContainers = this.$refs.scrollContainer.querySelectorAll(
        ".pdf-page-container",
      );
      pageContainers.forEach((container) => {
        this.observer.observe(container);
      });
    },

    handleIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const pageNumber = parseInt(entry.target.dataset.pageNumber);
          this.renderPage(pageNumber);
          // Stop observing once rendered
          this.observer.unobserve(entry.target);
        }
      });
    },

    async renderPage(pageNumber) {
      const pageState = this.pages.find((p) => p.pageNumber === pageNumber);
      if (!pageState || pageState.rendered) return;

      try {
        const page = await this.pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: this.scale });

        // Update page's size from generic skeleton
        this.pages[pageNumber - 1].width = viewport.width;
        this.pages[pageNumber - 1].height = viewport.height;

        // Get the canvas for this page
        // Note: refs inside v-for are arrays
        const canvasRef = this.$refs["pageCanvas" + pageNumber];
        const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;

        if (canvas) {
          const context = canvas.getContext("2d");
          const outputScale = window.devicePixelRatio || 1;
          const renderViewport = page.getViewport({ scale: this.scale * outputScale });

          canvas.width = renderViewport.width;
          canvas.height = renderViewport.height;
          canvas.style.width = viewport.width + "px";
          canvas.style.height = viewport.height + "px";

          const renderContext = {
            canvasContext: context,
            viewport: renderViewport,
          };

          await page.render(renderContext).promise;
        }

        // Render Text Layer
        const textLayerRef = this.$refs["textLayer" + pageNumber];
        const textLayerDiv = Array.isArray(textLayerRef)
          ? textLayerRef[0]
          : textLayerRef;

        if (textLayerDiv) {
          const textContent = await page.getTextContent();

          textLayerDiv.innerHTML = "";
          textLayerDiv.style.height = viewport.height + "px";
          textLayerDiv.style.width = viewport.width + "px";
          textLayerDiv.style.setProperty("--scale-factor", this.scale);

          await pdfjs.renderTextLayer({
            textContent: textContent,
            container: textLayerDiv,
            viewport: viewport,
            textDivs: [],
          }).promise;
        }

        pageState.rendered = true;
      } catch (err) {
        console.error(`Error rendering page ${pageNumber}:`, err);
      }
    },
  },
};
</script>

<style scoped>
.pdf-viewer-container {
  height: 100%;
}

.pdf-scroll-area {
  text-align: center;
}

.pdf-page-container {
  margin: 0 auto 20px auto;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
}

.pdf-loading,
.pdf-error {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--viva-grey);
  background-color: var(--viva-white);
  gap: 15px;
}

.pdf-loading i,
.pdf-error i {
  font-size: 32px;
  color: var(--viva-green);
}

.pdf-error i {
  color: var(--viva-orange);
}

.page-loading-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  z-index: 1;
}

.page-loading-placeholder i {
  font-size: 24px;
  color: var(--viva-green);
}

.textLayer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 1;
  line-height: 1;
}

.textLayer ::v-deep span {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0% 0%;
}

.textLayer ::v-deep br {
  display: none;
}

.textLayer ::selection {
  background: rgba(0, 0, 255, 0.3);
}

.textLayer ::v-deep .endOfContent {
  display: block;
  position: absolute;
  left: 0px;
  top: 100%;
  right: 0px;
  bottom: 0px;
  z-index: -1;
  cursor: default;
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -moz-user-select: none;
}

.textLayer ::v-deep .endOfContent.active {
  top: 0px;
}
</style>
